import React, { useState } from 'react';
import { AutoparkService, ICreateAutoparkRequest } from '../../services/autopark.service';
import { validateAutoparkForm, AutoparkValidationErrors } from '../../utils/autoparkValidation';
import PhoneInput from '../PhoneInput/PhoneInput';
import './AddAutoparkModal.css';

interface AddAutoparkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddAutoparkModal: React.FC<AddAutoparkModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<ICreateAutoparkRequest>({
    fullnameEmployee: '',
    fullnameOwner: '',
    phone: '',
    tradeName: '',
    legalName: '',
    email: '',
    inn: '',
    kpp: '',
    bik: '',
    bankName: '',
    currentAccount: '',
    actualAddress: '',
    legalAddress: '',
    index: '',
    ogrn: '',
    orgType: 'fns',
    externalId: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<AutoparkValidationErrors>({});

  const handleInputChange = (field: keyof ICreateAutoparkRequest, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Очищаем ошибку для текущего поля
    if (field in validationErrors && validationErrors[field as keyof AutoparkValidationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field as keyof AutoparkValidationErrors]: undefined,
      }));
    }
  };

  const handlePhoneChange = (numericValue: string, isValid: boolean) => {
    setFormData((prev) => ({
      ...prev,
      phone: numericValue,
    }));

    // Обновляем ошибку валидации на основе isValid из PhoneInput
    setValidationErrors((prev) => ({
      ...prev,
      phone: !numericValue ? 'Телефон обязателен' : !isValid ? 'Неверный формат телефона' : undefined,
    }));
  };

  const resetForm = () => {
    setFormData({
      fullnameEmployee: '',
      fullnameOwner: '',
      phone: '',
      tradeName: '',
      legalName: '',
      email: '',
      inn: '',
      kpp: '',
      bik: '',
      bankName: '',
      currentAccount: '',
      actualAddress: '',
      legalAddress: '',
      index: '',
      ogrn: '',
      orgType: 'fns',
      externalId: '',
    });
    setValidationErrors({});
    setError(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Валидируем форму
      const errors = validateAutoparkForm(formData);

      // Дополнительно проверяем телефон (если есть ошибка валидации)
      if (validationErrors.phone) {
        errors.phone = validationErrors.phone;
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setLoading(false);
        return;
      }

      // Создаем автопарк через API
      const newAutopark = await AutoparkService.createAutopark(formData);

      // Вызываем колбэк успешного создания
      if (onSuccess) {
        onSuccess();
      }

      // Сбрасываем форму и закрываем модалку
      resetForm();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Ошибка при создании автопарка');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="autopark-modal-overlay" onClick={handleOverlayClick}>
      <button className="autopark-modal-close" onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <g clip-path="url(#clip0_162_26)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12.0003 13.414L17.6573 19.071C17.8459 19.2532 18.0985 19.354 18.3607 19.3517C18.6229 19.3494 18.8737 19.2443 19.0591 19.0588C19.2445 18.8734 19.3497 18.6226 19.352 18.3604C19.3543 18.0982 19.2535 17.8456 19.0713 17.657L13.4143 12L19.0713 6.34303C19.2535 6.15443 19.3543 5.90182 19.352 5.63963C19.3497 5.37743 19.2445 5.12662 19.0591 4.94121C18.8737 4.7558 18.6229 4.65063 18.3607 4.64835C18.0985 4.64607 17.8459 4.74687 17.6573 4.92903L12.0003 10.586L6.34331 4.92903C6.15386 4.75137 5.90272 4.65439 5.64304 4.65861C5.38336 4.66283 5.1355 4.76791 4.95192 4.95162C4.76834 5.13533 4.66343 5.38326 4.6594 5.64295C4.65537 5.90263 4.75252 6.1537 4.93031 6.34303L10.5863 12L4.92931 17.657C4.8338 17.7493 4.75762 17.8596 4.70521 17.9816C4.6528 18.1036 4.62522 18.2348 4.62406 18.3676C4.62291 18.5004 4.64821 18.6321 4.69849 18.755C4.74877 18.8779 4.82302 18.9895 4.91692 19.0834C5.01081 19.1773 5.12246 19.2516 5.24536 19.3018C5.36825 19.3521 5.49993 19.3774 5.63271 19.3763C5.76549 19.3751 5.89671 19.3475 6.01872 19.2951C6.14072 19.2427 6.25106 19.1665 6.34331 19.071L12.0003 13.414Z"
              fill="#3563E9"
            />
          </g>
          <defs>
            <clipPath id="clip0_162_26">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>
      <div className="autopark-modal-content">
        <div className="autopark-modal-header">
          <h2 className="autopark-modal-title">Добавление нового автопарка</h2>
        </div>

        <div className="autopark-modal-body">
          <div className="autopark-form-row">
            <div className="autopark-form-group">
              <label className="autopark-form-label">ФИО сотрудника *</label>
              <input
                type="text"
                className={`autopark-form-input ${validationErrors.fullnameEmployee ? 'autopark-form-input--error' : ''}`}
                placeholder="ФИО сотрудника"
                value={formData.fullnameEmployee}
                onChange={(e) => handleInputChange('fullnameEmployee', e.target.value)}
              />
              {validationErrors.fullnameEmployee && (
                <span className="autopark-form-error">{validationErrors.fullnameEmployee}</span>
              )}
            </div>
            <div className="autopark-form-group">
              <label className="autopark-form-label">ФИО владельца *</label>
              <input
                type="text"
                className={`autopark-form-input ${validationErrors.fullnameOwner ? 'autopark-form-input--error' : ''}`}
                placeholder="ФИО владельца"
                value={formData.fullnameOwner}
                onChange={(e) => handleInputChange('fullnameOwner', e.target.value)}
              />
              {validationErrors.fullnameOwner && (
                <span className="autopark-form-error">{validationErrors.fullnameOwner}</span>
              )}
            </div>
          </div>

          <div className="autopark-form-row">
            <div className="autopark-form-group">
              <label className="autopark-form-label">Телефон *</label>
              <PhoneInput
                id="phone"
                customInput={true}
                className={`autopark-form-input ${validationErrors.phone ? 'autopark-form-input--error' : ''}`}
                placeholder="+7 (___) ___-__-__"
                value={formData.phone}
                onChangeValue={handlePhoneChange}
              />
              {validationErrors.phone && <span className="autopark-form-error">{validationErrors.phone}</span>}
            </div>
            <div className="autopark-form-group">
              <label className="autopark-form-label">Индекс *</label>
              <input
                type="text"
                className={`autopark-form-input ${validationErrors.index ? 'autopark-form-input--error' : ''}`}
                placeholder="Почтовый индекс"
                value={formData.index}
                onChange={(e) => handleInputChange('index', e.target.value)}
              />
              {validationErrors.index && <span className="autopark-form-error">{validationErrors.index}</span>}
            </div>
          </div>

          <div className="autopark-form-row">
            <div className="autopark-form-group">
              <label className="autopark-form-label">Торговое наименование *</label>
              <input
                type="text"
                className={`autopark-form-input ${validationErrors.tradeName ? 'autopark-form-input--error' : ''}`}
                value={formData.tradeName}
                onChange={(e) => handleInputChange('tradeName', e.target.value)}
              />
              {validationErrors.tradeName && <span className="autopark-form-error">{validationErrors.tradeName}</span>}
            </div>
            <div className="autopark-form-group">
              <label className="autopark-form-label">Юридическое наименование *</label>
              <input
                type="text"
                className={`autopark-form-input ${validationErrors.legalName ? 'autopark-form-input--error' : ''}`}
                value={formData.legalName}
                onChange={(e) => handleInputChange('legalName', e.target.value)}
              />
              {validationErrors.legalName && <span className="autopark-form-error">{validationErrors.legalName}</span>}
            </div>
          </div>

          <div className="autopark-form-row">
            <div className="autopark-form-group">
              <label className="autopark-form-label">Email *</label>
              <input
                type="email"
                className={`autopark-form-input ${validationErrors.email ? 'autopark-form-input--error' : ''}`}
                placeholder="email@mail.ru"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
              {validationErrors.email && <span className="autopark-form-error">{validationErrors.email}</span>}
            </div>
            <div className="autopark-form-group">
              <label className="autopark-form-label">Внешний ID</label>
              <input
                type="text"
                className="autopark-form-input"
                placeholder="Внешний идентификатор"
                value={formData.externalId}
                onChange={(e) => handleInputChange('externalId', e.target.value)}
              />
            </div>
          </div>

          <div className="autopark-form-section">
            <h3 className="autopark-form-section-title">Реквизиты автопарка</h3>

            <div className="autopark-form-row">
              <div className="autopark-form-group">
                <label className="autopark-form-label">ИНН (10 цифр) *</label>
                <input
                  type="text"
                  className={`autopark-form-input ${validationErrors.inn ? 'autopark-form-input--error' : ''}`}
                  value={formData.inn}
                  onChange={(e) => handleInputChange('inn', e.target.value)}
                  maxLength={10}
                />
                {validationErrors.inn && <span className="autopark-form-error">{validationErrors.inn}</span>}
              </div>
              <div className="autopark-form-group">
                <label className="autopark-form-label">КПП (9 цифр) *</label>
                <input
                  type="text"
                  className={`autopark-form-input ${validationErrors.kpp ? 'autopark-form-input--error' : ''}`}
                  value={formData.kpp}
                  onChange={(e) => handleInputChange('kpp', e.target.value)}
                  maxLength={9}
                />
                {validationErrors.kpp && <span className="autopark-form-error">{validationErrors.kpp}</span>}
              </div>
            </div>

            <div className="autopark-form-row">
              <div className="autopark-form-group">
                <label className="autopark-form-label">БИК (9 цифр) *</label>
                <input
                  type="text"
                  className={`autopark-form-input ${validationErrors.bik ? 'autopark-form-input--error' : ''}`}
                  value={formData.bik}
                  onChange={(e) => handleInputChange('bik', e.target.value)}
                  maxLength={9}
                />
                {validationErrors.bik && <span className="autopark-form-error">{validationErrors.bik}</span>}
              </div>
              <div className="autopark-form-group">
                <label className="autopark-form-label">Название банка *</label>
                <input
                  type="text"
                  className={`autopark-form-input ${validationErrors.bankName ? 'autopark-form-input--error' : ''}`}
                  placeholder="Название банка"
                  value={formData.bankName}
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                />
                {validationErrors.bankName && <span className="autopark-form-error">{validationErrors.bankName}</span>}
              </div>
            </div>

            <div className="autopark-form-row">
              <div className="autopark-form-group autopark-form-group-full">
                <label className="autopark-form-label">Расчетный счет (20 цифр) *</label>
                <input
                  type="text"
                  className={`autopark-form-input ${validationErrors.currentAccount ? 'autopark-form-input--error' : ''}`}
                  value={formData.currentAccount}
                  onChange={(e) => handleInputChange('currentAccount', e.target.value)}
                  maxLength={20}
                />
                {validationErrors.currentAccount && (
                  <span className="autopark-form-error">{validationErrors.currentAccount}</span>
                )}
              </div>
            </div>

            <div className="autopark-form-row">
              <div className="autopark-form-group">
                <label className="autopark-form-label">Фактический адрес *</label>
                <input
                  type="text"
                  className={`autopark-form-input ${validationErrors.actualAddress ? 'autopark-form-input--error' : ''}`}
                  value={formData.actualAddress}
                  onChange={(e) => handleInputChange('actualAddress', e.target.value)}
                />
                {validationErrors.actualAddress && (
                  <span className="autopark-form-error">{validationErrors.actualAddress}</span>
                )}
              </div>
              <div className="autopark-form-group">
                <label className="autopark-form-label">Юр адрес *</label>
                <input
                  type="text"
                  className={`autopark-form-input ${validationErrors.legalAddress ? 'autopark-form-input--error' : ''}`}
                  value={formData.legalAddress}
                  onChange={(e) => handleInputChange('legalAddress', e.target.value)}
                />
                {validationErrors.legalAddress && (
                  <span className="autopark-form-error">{validationErrors.legalAddress}</span>
                )}
              </div>
            </div>

            <div className="autopark-form-row">
              <div className="autopark-form-group ">
                <label className="autopark-form-label">ОГРН (13 цифр) *</label>
                <input
                  type="text"
                  className={`autopark-form-input ${validationErrors.ogrn ? 'autopark-form-input--error' : ''}`}
                  value={formData.ogrn}
                  onChange={(e) => handleInputChange('ogrn', e.target.value)}
                  maxLength={13}
                />
                {validationErrors.ogrn && <span className="autopark-form-error">{validationErrors.ogrn}</span>}
              </div>
              <div
                className={`autopark-form-group autopark-form-save ${validationErrors.ogrn && 'autopark-form-save__error'}`}
              >
                <button className="autopark-save-button" onClick={handleSave} disabled={loading}>
                  {loading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="autopark-modal-error">{error}</div>}

        {/* <div className="autopark-modal-footer">
          <button className="autopark-save-button" onClick={handleSave} disabled={loading}>
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default AddAutoparkModal;
